import React from 'react';

import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import AppDashedBlockButton from '../../../components/designe/buttons/block-buttons/AppDashedBlockButton';
import AppPrimaryBlockButton from '../../../components/designe/buttons/block-buttons/AppPrimaryBlockButton';
import AppFormSelectWrapper from '../../../components/designe/forms/app-form-select-wrapper/AppFormSelectWrapper';
import AppFormTextField from '../../../components/designe/forms/app-form-text-field/FormTextField';
import PageContainer from '../../../components/layout/page-container/PageContainer';
import AppDialog from '../../../components/reusable/app-dialog/AppDialog';
import SavedProgramSetSuperset from '../../../components/reusable/saved-program-set-superset-modal/SavedProgramSetSuperset';
import SetGroupEditTable from '../../../components/reusable/set-group-edit-table/SetGroupEditTable';
import TrainingActivityTypePicker from '../../../components/reusable/training-activity-type-picker';
import withRouter from '../../../hoc/with-router';
import { useScrollToFirstError } from '../../../hooks/use-scroll-to-first-error';
import { useUserSetPreferences } from '../../../hooks/use-user-set-preferences';
import { IProgramSet } from '../../../types/interfaces/program-set';
import { groupBy, orderBy } from '../../../utils/collections/array-utils';
import { getSetKey } from '../../../utils/training/set-utils';
import useEditTable from '../edit-training/useEditTable';
import { useStyles } from '../new-training/NewTraining.styles';
import { useStartNewTrainingPage } from './useStartNewTrainingPage';

function StartNewTraining() {
  const theme = useTheme();
  const { classes } = useStyles(theme);
  const { showUploadSetButton } = useUserSetPreferences();
  const {
    t,
    formik,
    isLoading,
    isSubmitDisabled,
    defaultTrainingName,
    userProgramItems,
    onProgramItemChange,
    exerciseResults,
    onAddEmptyTrainingSet,
    onAddTrainingSetFromSavedProgramSet,
    showSavedTrainingModal,
    setShowSavedTrainingModal,
  } = useStartNewTrainingPage();

  const trainingSetGroups = groupBy(
    orderBy(formik.values.trainingSets, 'setNumber'),
    'setNumber',
  );

  const useSetGroupEditTable = useEditTable(formik, {
    exerciseResults,
  });

  useScrollToFirstError(formik);

  return (
    <PageContainer title={t('trainings.startEmptyQuestion')} backButton>
      <Box className={classes.addTrainingWrapper}>
        <form
          onSubmit={formik.handleSubmit}
          className={classes.addTrainingForm}>
          <Box paddingTop={1} display="flex" flexDirection="column" gap={1}>
            <AppFormTextField
              label={t('common.title')}
              fullWidth
              autoFocus
              placeholder={defaultTrainingName}
              name="title"
              formik={formik}
            />
            <TrainingActivityTypePicker formik={formik} fullWidth />
            <AppFormSelectWrapper
              data-onboarding="newTraining.programSelector"
              label={t('programs.programItem')}
              name="programItem"
              fieldName="title"
              options={userProgramItems}
              formik={formik}
              placeholder={t('programs.selectProgramItem')}
              onChange={(e) => onProgramItemChange(e)}
            />
          </Box>

          {formik.values.trainingSets &&
            formik.values.trainingSets.length > 0 &&
            Object.values(trainingSetGroups).map((trainingSetGroup) => (
              <Box marginTop={1} key={getSetKey(trainingSetGroup[0])}>
                <SetGroupEditTable
                  sets={trainingSetGroup}
                  useSetGroupEditTable={useSetGroupEditTable}
                />
              </Box>
            ))}

          <Box className={classes.addTrainingSetFooter}>
            <AppDashedBlockButton
              data-onboarding="newTraining.addTrainingSet"
              fullWidth
              onClick={() => onAddEmptyTrainingSet()}
              startIcon={<AddIcon />}
              yPadding={1}>
              {t('trainings.addTrainingSet')}
            </AppDashedBlockButton>
            {showUploadSetButton && (
              <AppDashedBlockButton
                fullWidth
                onClick={() => setShowSavedTrainingModal(true)}
                startIcon={<FileUploadIcon />}
                yPadding={1}>
                {t('trainings.addSavedSet')}
              </AppDashedBlockButton>
            )}
          </Box>

          <Box className={classes.addTrainingButtonWrapper}>
            <AppPrimaryBlockButton
              data-onboarding="newTraining.startTraining"
              type="submit"
              fullWidth
              loader={isLoading}
              disabled={isSubmitDisabled}
              startIcon={<PlayArrowIcon />}>
              {t('trainings.startNewTraining')}
            </AppPrimaryBlockButton>
          </Box>
        </form>
      </Box>

      {showUploadSetButton && (
        <AppDialog
          open={!!showSavedTrainingModal}
          onClose={() => setShowSavedTrainingModal(false)}
          title={t('trainings.savedProgramSets')}>
          <SavedProgramSetSuperset
            onCancel={() => setShowSavedTrainingModal(false)}
            onSubmitSavedProgramSet={(value: {
              programSets: IProgramSet[];
              isSuperset: boolean;
            }) => {
              setShowSavedTrainingModal(false);
              onAddTrainingSetFromSavedProgramSet(
                value.programSets,
                value.isSuperset,
              );
            }}
          />
        </AppDialog>
      )}
    </PageContainer>
  );
}

export default withRouter(StartNewTraining);
